import type { AuroraControlId } from 'api-validations/aurora'
import { type PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { useLocalization } from 'shared-ui/localization'

import { type Awaits, auroraControls, useAuroraControlsStore } from './auroraControlsStore'

export const AuroraControl = ({
  id,
  value,
  awaits,
  children
}: PropsWithChildren<{
  id: AuroraControlId
  value: string
  awaits?: Awaits
}>) => {
  useEffect(() => {
    auroraControls.offer(id, { value, awaits })
  }, [id, value, awaits])
  useEffect(() => () => auroraControls.withdraw(id), [id])

  const slot = useAuroraControlsStore((state) =>
    state.shown.includes(id) ? state.slots[id] : undefined
  )
  const inline = useRef<HTMLDivElement>(null)
  const [host] = useState(() => {
    const element = document.createElement('div')
    element.className = 'contents'
    return element
  })

  useLayoutEffect(() => {
    ;(slot ?? inline.current)?.appendChild(host)
    return () => host.remove()
  }, [slot, host])

  return (
    <>
      {slot !== undefined && <ShownInAurora id={id} />}
      <div ref={inline} className="contents" />
      {createPortal(children, host)}
    </>
  )
}

const ShownInAurora = ({ id }: { id: AuroraControlId }) => {
  const { t } = useLocalization()
  return (
    <div className="pan-detail flex items-center justify-between gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-gray-500">
      <span className="flex items-center gap-2">
        <Icon icon={SpriteMap.AuroraPanoramaIcon} className="size-4 text-green-500" />
        {t(($) => $.auroraControls.titles[id])} · {t(($) => $.auroraControls.shownInAurora)}
      </span>
      <button
        type="button"
        onClick={() => auroraControls.hide(id)}
        className="font-bold text-green-600 hover:text-green-700"
      >
        {t(($) => $.auroraControls.showHere)}
      </button>
    </div>
  )
}
